import React, { FC, useRef, useState } from 'react';
import Image from 'components/image/Image';
import { JobCardActionEnum } from 'core/enum/jobCardAction';
import { STAGE_TYPES } from 'core/constants/jobCardConstant';
import { MenuItem, Select } from '@material-ui/core';

import './Index.scss';
import { useNavigate } from 'react-router';
import { DynamicActionButton } from 'components/newButtons/DynamicActionButton';
import { IndustryList } from 'components/IndustryList';
import { JobService } from 'services/jobs/job';
import { clampText } from 'helpers/sharedFunctions';
import useLocales from 'hooks/useLocales';
import { CANDIDATE_ROUTES } from 'core/constants/candidateRoutes';
import Popup from 'components/shared/Popups/Popup/Index';
import ConfirmPopup from 'components/shared/Popups/Confirm/Index';
import ApplicationHistoryList from 'components/shared/ApplicationHistoryList/Index';
import { getCultureFitLabel } from 'helpers/cultureFitLabel';
import { formatTimestampedLine } from 'helpers/applicationHistory';

const actions = {
  pending: [
    JobCardActionEnum.ACCEPT,
    JobCardActionEnum.CANCEL,
    JobCardActionEnum.MORE_INFO
  ],
  accepted: [JobCardActionEnum.PROFILE_VIEW, JobCardActionEnum.MORE_INFO]
};

const IncomingRequests: FC<any> = ({
  id,
  name,
  sub_title,
  email,
  icon,
  score,
  culture_score,
  date,
  history,
  industry,
  stage,
  setOpen,
  status,
  cancel,
  candidateId,
  isCandidate,
  jobId,
  acceptRequest,
  declineRequest,
  jobTitle,
  selectedBy,
  isSelectedCandidate = false,
  onStageChange,
  onDelete,
  showDelete = false
}): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showIndustries, setShowIndustries] = useState(false);
  const industriesBtnRef = useRef<HTMLSpanElement>(null);

  const value = STAGE_TYPES.find((el: any) => el.value === stage);
  const currentActions = status === 2 ? actions.accepted : actions.pending;
  const canEditStage = isSelectedCandidate || (!isCandidate && status === 2);
  const canShowDelete = showDelete || isSelectedCandidate;

  const handleStageChange = async (requestId: number, stageValue: any) => {
    if (onStageChange) {
      await onStageChange(requestId, { stage: stageValue });
      return;
    }
    await JobService.manageJob(requestId, { stage: stageValue });
  };

  const handleDelete = () => {
    if (showDelete && !isSelectedCandidate) {
      setShowDeleteConfirm(true);
      return;
    }
    confirmDelete();
  };

  const confirmDelete = () => {
    if (onDelete) {
      onDelete(id);
    } else if (cancel) {
      cancel(id);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <div className="IRCard" style={{ position: 'relative' }}>
      <div
        className="IRCard__left"
        style={{
          width: isCandidate ? '400px' : '450px'
        }}
      >
        <div onClick={() => setOpen(jobId || id, 'incoming-requests')}>
          <Image
            url={icon}
            name={!icon ? 'no-avatar.svg' : ''}
            className={
              isCandidate
                ? 'IRCard__left-image-candidate'
                : 'IRCard__left-image'
            }
            objectFit="cover"
          />
        </div>
        <div className="IRCard__left__info">
          <div className="IRCard__left__info_name">
            <div className="IRCard__left__info-name-container">
              <span className="IRCard__left__info-title">{name || email}</span>
              <span className="IRCard__left__info-job-title">{jobTitle}</span>
            </div>
            {industry && (
              <IndustryList
                industry={industry}
                showIndustries={showIndustries}
                setShowIndustries={setShowIndustries}
                industriesBtnRef={industriesBtnRef}
              />
            )}
          </div>
          <span className="IRCard__left__info-sub">
            {clampText(sub_title, 45)}
          </span>
          {canEditStage ? (
            <Select
              className="IRCard__left__info-status"
              value={value?.value ?? 0}
              onChange={(e) => handleStageChange(id, e.target.value)}
              onClose={() => setIsOpen(false)}
              onOpen={() => setIsOpen(true)}
              onClick={() => setIsOpen(!isOpen)}
              IconComponent={() => (
                <>
                  <Image
                    className={`down-arrow ${isOpen ? 'active' : ''}`}
                    name="down-arrow-white.svg"
                  />
                </>
              )}
              inputProps={{ 'aria-label': 'Without label' }}
            >
              {STAGE_TYPES?.map((el: any) => (
                <MenuItem value={el.value} key={el.value}>
                  {el.to_show}
                </MenuItem>
              ))}
            </Select>
          ) : (
            !isCandidate &&
            !isSelectedCandidate && (
              <span className="new_application">{t('New application')}</span>
            )
          )}
        </div>
      </div>
      <div className="IRCard__middle">
        <div className="IRCard__middle__percents">
          <span className="IRCard__middle__matches-title">
            {score || 0}% {t('Talent Match')}
          </span>
          <span className="IRCard__middle__matches-title">
            {getCultureFitLabel(culture_score, t)}
          </span>
        </div>
        <div className="IRCard__middle__details">
          <div className="IRCard__middle__details__item">
            <span className="IRCard__middle__details__item-desc">
              {isCandidate
                ? formatTimestampedLine('Message send on', date, t, {
                    includeOn: false
                  })
                : isSelectedCandidate
                  ? formatTimestampedLine('Selected', date, t, {
                      recruiterName: selectedBy?.name
                    })
                  : formatTimestampedLine('Applied on', date, t, {
                      includeOn: false
                    })}
            </span>
          </div>

          <ApplicationHistoryList
            history={history}
            classPrefix="IRCard"
            t={t}
          />
        </div>
      </div>
      <div className="IRCard__right">
        <div className="IRCard__right__actions">
          {!isCandidate && !isSelectedCandidate && status === 0 && (
            <>
              <DynamicActionButton
                label={t('Accept')}
                backgroundColor="#0fb398"
                onClick={() => acceptRequest && acceptRequest(id)}
              />
              <DynamicActionButton
                label={t('Decline')}
                backgroundColor="red"
                onClick={() => declineRequest && declineRequest(id)}
              />
            </>
          )}
          {isCandidate && status === 1 && (
            <>
              <DynamicActionButton
                label={t('Accept')}
                backgroundColor="#0fb398"
                onClick={() => acceptRequest && acceptRequest(id)}
              />
              <DynamicActionButton
                label={t('Decline')}
                backgroundColor="red"
                onClick={() => declineRequest && declineRequest(id)}
              />
            </>
          )}
          {canShowDelete && (
            <DynamicActionButton
              label={t('Delete')}
              backgroundColor="red"
              onClick={handleDelete}
            />
          )}
          {!isSelectedCandidate &&
            currentActions.map((action, index) => (
              <React.Fragment key={index}>
                {action === JobCardActionEnum.MORE_INFO && status !== 2 && (
                  <DynamicActionButton
                    label={t('More Info')}
                    backgroundColor="#BEA0E5"
                    onClick={() => setOpen(jobId || id, 'incoming-requests')}
                  />
                )}

                {action === JobCardActionEnum.PROFILE_VIEW &&
                  status === 2 &&
                  !isCandidate &&
                  candidateId && (
                    <DynamicActionButton
                      label={t('Profile')}
                      backgroundColor="#A0D5E5"
                      onClick={() =>
                        navigate(CANDIDATE_ROUTES.profile(candidateId))
                      }
                    />
                  )}

                {action === JobCardActionEnum.MORE_INFO && status === 2 && (
                  <DynamicActionButton
                    label={t('More Info')}
                    backgroundColor="#BEA0E5"
                    onClick={() => setOpen(jobId || id, 'teaser-view')}
                  />
                )}
              </React.Fragment>
            ))}
          {isSelectedCandidate && (
            <>
              {candidateId && (
                <DynamicActionButton
                  label={t('Profile')}
                  backgroundColor="#A0D5E5"
                  onClick={() =>
                    navigate(CANDIDATE_ROUTES.profile(candidateId))
                  }
                />
              )}
              <DynamicActionButton
                label={t('More Info')}
                backgroundColor="#BEA0E5"
                onClick={() => setOpen(jobId || id, 'incoming-requests')}
              />
            </>
          )}
        </div>
      </div>
      {showDeleteConfirm && (
        <Popup
          title={t('Are you sure?')}
          onClose={() => setShowDeleteConfirm(false)}
        >
          <ConfirmPopup
            approveTitle="Yes"
            cancelTitle="No"
            description={t('Deleting this application will allow the candidate to apply for this job again. Are you sure you would like to continue?')}
            onCancel={() => setShowDeleteConfirm(false)}
            onApprove={confirmDelete}
            t={t}
          />
        </Popup>
      )}
    </div>
  );
};

export default IncomingRequests;
